import { useState } from 'react';
import { sealEnvelope, type ContactInput, type IssuedKey, type EnvelopeV2 } from '../lib/envelope';
import { validatePassphrase, calculatePassphraseStrength } from '@agl/shared/crypto';
import { loadConnection, putEnvelope, providerMeta } from '../lib/providers';
import { Stepper, type StepDef } from '../components/Stepper';
import { StorageSetup } from './Storage';

type Saved = { envelope: EnvelopeV2; issuedKeys: IssuedKey[]; label: string; id: string };

const STORAGE = 1;
const DOCUMENT = 2;
const PASSPHRASE = 3;
const RECIPIENTS = 4;

export function Seal() {
  const [conn, setConn] = useState(loadConnection());
  const storageReady = !!conn?.verifiedAt;

  // Storage is step one, so an unconfigured page opens there. Someone who has
  // already connected starts on the document instead of being walked past a
  // step they finished weeks ago.
  const [step, setStep] = useState(storageReady ? DOCUMENT : STORAGE);

  const [file, setFile] = useState<File | null>(null);
  const [passphrase, setPassphrase] = useState('');
  const [contacts, setContacts] = useState<ContactInput[]>([{ name: '', email: '' }]);
  const [saved, setSaved] = useState<Saved | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const strength = calculatePassphraseStrength(passphrase);
  const named = contacts.filter((c) => c.name.trim() && c.email.trim());
  const passphraseOk = validatePassphrase(passphrase).isValid;

  const steps: StepDef[] = [
    {
      n: STORAGE,
      title: 'Storage',
      done: storageReady,
      summary: storageReady ? providerMeta(conn!.provider).name : 'not connected',
    },
    {
      n: DOCUMENT,
      title: 'Document',
      done: !!file,
      summary: file ? file.name : undefined,
    },
    {
      n: PASSPHRASE,
      title: 'Passphrase',
      done: passphraseOk,
      summary: passphraseOk ? 'set' : undefined,
    },
    {
      n: RECIPIENTS,
      title: 'Recipients',
      done: step > RECIPIENTS || (!!file && passphraseOk && step === RECIPIENTS),
      summary: named.length ? `${named.length} person${named.length > 1 ? 's' : ''}` : undefined,
    },
  ];

  function update(i: number, patch: Partial<ContactInput>) {
    setContacts((cs) => cs.map((c, n) => (n === i ? { ...c, ...patch } : c)));
  }

  async function seal() {
    setError(null);
    if (!file) return setError('Choose a document first.');
    const check = validatePassphrase(passphrase);
    if (!check.isValid) return setError(check.error ?? 'That passphrase is too short.');

    setBusy(true);
    try {
      const { envelope, issuedKeys } = await sealEnvelope(file, passphrase, named);
      setSaved({ envelope, issuedKeys, label: file.name, id: crypto.randomUUID().replace(/-/g, '') });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  if (saved) {
    return (
      <Result
        saved={saved}
        onReset={() => {
          setSaved(null);
          setFile(null);
          setPassphrase('');
          setContacts([{ name: '', email: '' }]);
          setStep(loadConnection()?.verifiedAt ? DOCUMENT : STORAGE);
        }}
      />
    );
  }

  return (
    <>
      <Stepper steps={steps} current={step} onGo={setStep} />

      {step === STORAGE && (
        <section className="card">
          <p className="eyebrow">Step one</p>
          <h2>Where your documents will live</h2>
          {storageReady ? (
            <>
              <p className="lede">
                Connected to {providerMeta(conn!.provider).name}. Envelopes are
                written straight there from this tab.
              </p>
              <button className="primary" onClick={() => setStep(DOCUMENT)}>
                Continue
              </button>
            </>
          ) : (
            <>
              <p className="lede">
                Connect storage you own, and envelopes go straight there. You can
                also skip this and download each envelope as a file instead — the
                encryption is identical either way.
              </p>
              <StorageSetup
                onConnected={() => {
                  setConn(loadConnection());
                  setStep(DOCUMENT);
                }}
              />
              <div className="divider" />
              <button onClick={() => setStep(DOCUMENT)}>
                Skip — I'll download the file myself
              </button>
            </>
          )}
        </section>
      )}

      {step === DOCUMENT && (
        <section className="card">
          <p className="eyebrow">Step two</p>
          <h2>What are you protecting?</h2>
          <p className="lede">It is encrypted here and never leaves this tab unencrypted.</p>
          <input type="file" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
          {file && (
            <p className="muted" style={{ margin: '.6rem 0 1rem' }}>
              {file.name} — {(file.size / 1024).toFixed(1)} KB
            </p>
          )}
          <div className="nav">
            <button onClick={() => setStep(STORAGE)}>Back</button>
            <button className="primary" disabled={!file} onClick={() => setStep(PASSPHRASE)}>
              Continue
            </button>
          </div>
        </section>
      )}

      {step === PASSPHRASE && (
        <section className="card">
          <p className="eyebrow">Step three</p>
          <h2>Choose a passphrase</h2>
          <p className="lede">
            This unlocks your own copy. The key is derived here with 600,000 rounds
            of PBKDF2, which is why it takes a moment.
          </p>
          <label>
            Passphrase
            <input
              type="password"
              value={passphrase}
              onChange={(e) => setPassphrase(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && passphraseOk && setStep(RECIPIENTS)}
              placeholder="At least 12 characters"
            />
          </label>
          {passphrase && (
            <div className="meter" aria-label={`Strength ${strength} of 4`}>
              {[0, 1, 2, 3].map((i) => (
                <span key={i} className={i < strength ? `on s${strength}` : ''} />
              ))}
            </div>
          )}
          <p className="hint">
            There is no reset. If you forget this, the document cannot be recovered
            by anyone — that is what makes it private.
          </p>
          <div className="nav">
            <button onClick={() => setStep(DOCUMENT)}>Back</button>
            <button className="primary" disabled={!passphraseOk} onClick={() => setStep(RECIPIENTS)}>
              Continue
            </button>
          </div>
        </section>
      )}

      {step === RECIPIENTS && (
        <section className="card">
          <p className="eyebrow">Step four</p>
          <h2>Who may open it</h2>
          <p className="lede">
            Each person gets their own key. Losing or replacing one never affects
            the others, and none of them opens anyone else's copy. Leave this empty
            if only you should be able to open it.
          </p>
          {contacts.map((c, i) => (
            <div className="row" key={i}>
              <label>
                Name
                <input value={c.name} onChange={(e) => update(i, { name: e.target.value })} />
              </label>
              <label>
                Email
                <input value={c.email} onChange={(e) => update(i, { email: e.target.value })} />
              </label>
              {contacts.length > 1 && (
                <button
                  type="button"
                  style={{ marginTop: '1.55rem' }}
                  onClick={() => setContacts((cs) => cs.filter((_, n) => n !== i))}
                >
                  Remove
                </button>
              )}
            </div>
          ))}
          <button type="button" onClick={() => setContacts((cs) => [...cs, { name: '', email: '' }])}>
            Add another
          </button>

          {error && <p className="error">{error}</p>}

          <div className="nav">
            <button onClick={() => setStep(PASSPHRASE)}>Back</button>
            <button className="primary" disabled={busy || !file} onClick={() => void seal()}>
              {busy
                ? 'Encrypting…'
                : `Encrypt${named.length ? ` for ${named.length}` : ''}`}
            </button>
          </div>
        </section>
      )}
    </>
  );
}

function Result({ saved, onReset }: { saved: Saved; onReset: () => void }) {
  const [storing, setStoring] = useState(false);
  const [storedAt, setStoredAt] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const conn = loadConnection();
  const json = JSON.stringify(saved.envelope, null, 2);

  function download() {
    const url = URL.createObjectURL(new Blob([json], { type: 'application/json' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `${saved.label}.agenvelope.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  async function store() {
    if (!conn) return;
    setStoring(true);
    setError(null);
    try {
      const { path } = await putEnvelope(conn, saved.id, json);
      setStoredAt(path);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setStoring(false);
    }
  }

  return (
    <>
      <section className="card">
        <p className="eyebrow">Sealed</p>
        <h2>Your envelope is ready</h2>
        <p className="lede">
          One file, useless without a key. Its contents, its filename and who it is
          for are all encrypted — safe to keep anywhere.
        </p>

        {conn?.verifiedAt ? (
          <div className="nav start">
            <button className="primary" disabled={storing || !!storedAt} onClick={() => void store()}>
              {storedAt ? 'Saved to your storage' : storing ? 'Saving…' : 'Save to my storage'}
            </button>
            <button onClick={download}>Download a copy</button>
          </div>
        ) : (
          <div className="nav start">
            <button className="primary" onClick={download}>
              Download the envelope
            </button>
          </div>
        )}
        {storedAt && (
          <p className="ok">
            Stored in your bucket as <code>{storedAt}</code>
          </p>
        )}
        {error && <p className="error">{error}</p>}
      </section>

      <section className="card">
        <p className="eyebrow">Hand these out</p>
        <h2>{saved.issuedKeys.length ? 'The keys, shown once' : 'No recipients'}</h2>
        {saved.issuedKeys.length ? (
          <>
            <p className="warn">
              These are not saved anywhere and are not inside the envelope. Once you
              leave this page they cannot be recovered — copy them now.
            </p>
            <ul className="keys">
              {saved.issuedKeys.map((k) => (
                <li key={k.email}>
                  <strong>{k.name}</strong> <span className="muted">{k.email}</span>
                  <code>{k.keyString}</code>
                  <button onClick={() => void navigator.clipboard.writeText(k.keyString)}>Copy</button>
                </li>
              ))}
            </ul>
          </>
        ) : (
          <p className="lede">Only your passphrase opens this envelope.</p>
        )}
        <button onClick={onReset}>Protect another document</button>
      </section>
    </>
  );
}
